import "./user-activities.css";
import { PRODUCT, WEB_ROUTES } from "./config";

export type JourneyStatus =
  | "shipped"
  | "source"
  | "in-progress"
  | "roadmap";

export interface ActivityLink {
  label: string;
  href: string;
}

export interface EssentialJourney {
  id: string;
  title: string;
  platform: string;
  status: JourneyStatus;
  statusLabel: string;
  releaseTruth: string;
  prerequisites: string[];
  steps: string[];
  expected: string[];
  evidence: ActivityLink[];
}

export interface AutomatedTestGroup {
  title: string;
  purpose: string;
  examples: string[];
  links: ActivityLink[];
}

const GITHUB = "https://github.com/markitics/agenda";
const issue = (number: number): string => `${GITHUB}/issues/${number}`;
const milestone = (number: number): string => `${GITHUB}/milestone/${number}`;
const source = (path: string): string => `${GITHUB}/blob/main/${path}`;

export const EVIDENCE_DATE = "10 August 2026";

export const ESSENTIAL_JOURNEYS: EssentialJourney[] = [
  {
    id: "UA-01",
    title: "Complete a clean first launch without seeing anybody else's cards",
    platform: "iPhone · prepared clean test install only",
    status: "source",
    statusLabel: "Current source · candidate proof open",
    releaseTruth:
      "The clean-start and onboarding source is merged. Build 37 is not yet an accepted public candidate, so this is not a claim about a downloadable App Store build.",
    prerequisites: [
      "Use a prepared clean test device or simulator. Never delete the preserved data on Mark's normal test phone just to run this journey.",
      "Record the visible product name, version, build, device model, and iOS version before judging the result.",
    ],
    steps: [
      "Launch Convoful.",
      "Tap Continue through the introduction, or tap Skip intro and then Continue to Convoful.",
      "Open Deck, Manage, History, and Search in turn.",
    ],
    expected: [
      "No legacy Mark/Maya card, private note, source, photo, or history appears.",
      "The app opens an empty, actionable workspace and does not silently write completion before the final Continue.",
      "A preserved upgrade is a different test: it must keep legitimate existing local data and bypass destructive onboarding.",
    ],
    evidence: [
      { label: "Privacy gate #444", href: issue(444) },
      { label: "Onboarding gate #595", href: issue(595) },
      { label: "First-submission milestone", href: milestone(17) },
    ],
  },
  {
    id: "UA-02",
    title: "Log in or create an account with one email-link flow",
    platform: "iPhone and Web",
    status: "source",
    statusLabel: "Merged service and UI · candidate acceptance open",
    releaseTruth:
      "Self-service account source and controlled acceptance evidence are merged. Exact-candidate, ordinary-person acceptance remains part of the release gate.",
    prerequisites: [
      "Use a controlled test mailbox. Never use a real stranger's address or publish the address in evidence.",
      "For iPhone, open Sync. For Web, open convoful.com/app.",
    ],
    steps: [
      "Enter the test email address.",
      "Tap or click Email me a link.",
      "Confirm the generic Email sent message, open the newest link from the controlled mailbox, and return to Convoful.",
      "Refresh signed-in devices, then sign out this device and repeat the sign-in once.",
    ],
    expected: [
      "The request screen does not reveal whether the address already had an account.",
      "The link creates or opens exactly one account and one owner workspace; replaying the link does not create another account.",
      "Sign out this device revokes only that session; other sessions remain unless Sign out everywhere is chosen.",
    ],
    evidence: [
      { label: "Signup #323", href: issue(323) },
      { label: "Account parent #202", href: issue(202) },
      { label: "Accounts milestone", href: milestone(14) },
    ],
  },
  {
    id: "UA-03",
    title: "Create and switch audiences without mixing private decks",
    platform: "iPhone · current source",
    status: "source",
    statusLabel: "Current source · production/device proof open",
    releaseTruth:
      "Audience creation and selection are present in current source. Build 37 and the matching production Worker still need complete release acceptance.",
    prerequisites: [
      "Be signed in with a test owner workspace.",
      "Use privacy-safe audience names created for testing.",
    ],
    steps: [
      "Open Search and open the audience selector.",
      "Choose New audience, type a name, and tap Create.",
      "Select the new audience, then switch back to the prior audience.",
      "If a conversation is active, attempt the switch and respond to the End conversation and switch confirmation.",
    ],
    expected: [
      "The new audience persists and becomes selectable after authoritative refresh.",
      "Deck, History, Search, and statistics change scope together; private rows from the other audience do not appear.",
      "An active conversation cannot silently change audience underneath the tester.",
    ],
    evidence: [
      { label: "Audience model #30", href: issue(30) },
      { label: "Audience creation #623", href: issue(623) },
    ],
  },
  {
    id: "UA-04",
    title: "Save a link, text, or photo from another iPhone app",
    platform: "iPhone Share Sheet",
    status: "shipped",
    statusLabel: "Previously shipped · expand acceptance",
    releaseTruth:
      "Basic Share Sheet capture landed in Build 3. Multi-audience and Cards-versus-Library assignment has shipped evidence, but the complete #246 acceptance matrix remains open.",
    prerequisites: [
      "Use a privacy-safe page such as example.com or a synthetic photo.",
      "If the installed build still says Agenda!, retain that historical name in the test record.",
    ],
    steps: [
      "In Safari, open the safe page and tap Share.",
      "Tap Convoful or Agenda! in the activity row.",
      "Choose one or more audiences; for each, choose Conversation Cards, Library, or both when those controls are present.",
      "Optionally add Your private note, choose Theme and Importance, then tap Save.",
      "Tap Done, reopen Convoful, and open the saved-share summary if it appears.",
    ],
    expected: [
      "One recoverable save is created; a retry or relaunch does not duplicate it.",
      "Conversation Cards remains private. Library is never enabled without an explicit choice.",
      "The saved item appears only for the exact selected audience and destination.",
    ],
    evidence: [
      { label: "Share verification", href: source("docs/SHARE_EXTENSION_VERIFICATION.md") },
      { label: "Multi-audience share #246", href: issue(246) },
      { label: "Capture milestone", href: milestone(7) },
    ],
  },
  {
    id: "UA-05",
    title: "Create a card inside Convoful",
    platform: "iPhone Manage",
    status: "in-progress",
    statusLabel: "Link and Text available · Photo upcoming",
    releaseTruth:
      "Installed-build evidence covers Link and Text creation. Photo creation and the full cross-platform acceptance criteria remain open in #92.",
    prerequisites: [
      "Open Manage in an owner test account.",
      "Prepare a safe URL and short synthetic text.",
    ],
    steps: [
      "Tap New card.",
      "Choose Link, paste the URL, set the title/private fields and exact audience destination, then tap Save.",
      "Tap View card, return to Manage, and repeat with Text.",
      "Cancel one changed draft, reopen New card, and confirm the app offers truthful recovery instead of silently losing or saving it.",
    ],
    expected: [
      "Each successful Save creates one findable card in the exact selected audience.",
      "Cancel does not publish the draft. Retry does not create duplicates.",
      "If Photo is absent, record Not available in this build; do not mark the Link/Text journey failed.",
    ],
    evidence: [
      { label: "New Card #92", href: issue(92) },
      { label: "Capture milestone", href: milestone(7) },
    ],
  },
  {
    id: "UA-06",
    title: "Find a private card and edit owner-only context",
    platform: "iPhone Search and Manage",
    status: "in-progress",
    statusLabel: "Lexical/offline search available · semantic path upcoming",
    releaseTruth:
      "Private keyword/Summary search and editors have automated coverage. Reliable cross-phone acceptance and the production semantic index/route remain open.",
    prerequisites: [
      "Choose an audience containing a synthetic card with a distinctive word.",
      "Keep Notes, Summary, keywords, and Shared Notes visibly distinct.",
    ],
    steps: [
      "Tap Search and type the distinctive word.",
      "Open the result's private editor.",
      "Change the private Summary or keywords and tap Save.",
      "Return to results, clear the query, then search for the new term.",
      "Open Manage, edit Your private note, cancel once, then save a deliberate edit.",
    ],
    expected: [
      "Only cards eligible for the selected audience appear.",
      "Cancelled private edits do not persist; saved edits remain owner-only and never become recipient Library copy.",
      "If semantic service is unavailable, the UI says it is using the offline/private fallback instead of claiming semantic results.",
    ],
    evidence: [
      { label: "Search #27", href: issue(27) },
      { label: "Enrichment #28", href: issue(28) },
      { label: "Search milestone", href: milestone(8) },
    ],
  },
  {
    id: "UA-07",
    title: "Start a conversation and reveal exactly one card",
    platform: "iPhone Deck",
    status: "shipped",
    statusLabel: "Core journey since Build 3",
    releaseTruth:
      "The private reveal ritual is the core shipped behavior. Build 33 added rapid-repeat protection; real-device responsiveness under heavy state remains open in #400.",
    prerequisites: [
      "Select a test audience with at least two eligible cards.",
      "Confirm no private prompt, source text, URL, or accessibility value is visible face down.",
    ],
    steps: [
      "Open Deck and tap Start conversation when shown.",
      "Tap the face-down card once.",
      "Immediately tap it repeatedly, then wait for the animation or Reduce Motion transition to finish.",
      "Tap Next card once.",
    ],
    expected: [
      "The first tap produces immediate acknowledgement and reveals the prompt exactly once.",
      "Rapid taps cannot flip the same card back, execute late, or expose the next prompt.",
      "Next card advances once and does not repeat or skip because of the prior taps.",
    ],
    evidence: [
      { label: "Launch review #12", href: issue(12) },
      { label: "Gesture performance #400", href: issue(400) },
      { label: "Physical play milestone", href: milestone(12) },
    ],
  },
  {
    id: "UA-08",
    title: "Mark a card discussed or postponed, then Undo",
    platform: "iPhone Deck and History",
    status: "shipped",
    statusLabel: "Core outcome journey",
    releaseTruth:
      "Discussed, postponed, History, and Undo have shipped and extensive UI coverage. Authoritative outcome recovery remains release-sensitive.",
    prerequisites: [
      "Start a conversation and reveal a synthetic card.",
    ],
    steps: [
      "Swipe or tap the visible Discussed action.",
      "Tap Undo before it expires and verify the same card returns.",
      "Mark it Discussed again; reveal another card and choose Postpone.",
      "Open History, choose each entry, and then return to Deck.",
    ],
    expected: [
      "Each outcome records once, advances once, and can be undone only within the visible window.",
      "History shows the action and time without exposing a card whose assignment has since been revoked.",
      "Discussed and postponed remain distinct; neither silently changes Library consumption state.",
    ],
    evidence: [
      { label: "Sessions milestone", href: milestone(11) },
      { label: "Conversation UI tests", href: source("AgendaUITests/AgendaUITests.swift") },
    ],
  },
  {
    id: "UA-09",
    title: "Resume the same conversation after relaunch",
    platform: "iPhone Deck",
    status: "shipped",
    statusLabel: "Offline continuity shipped",
    releaseTruth:
      "Local continuity has shipped and is tested. Signed-out and reauthenticated authority edge cases remain part of #327 and the release batch.",
    prerequisites: [
      "Start a conversation and postpone one card.",
      "Note the privacy-safe continuity code and remaining count, not the prompt text.",
    ],
    steps: [
      "Send Convoful to the background, then force-quit it.",
      "Relaunch and open Deck.",
      "Compare the continuity code, selected audience, remaining count, and parked state.",
      "Tap End conversation, confirm, then start a new one.",
    ],
    expected: [
      "The same active session resumes without revealing a new prompt or reviving a completed card.",
      "Ending preserves History but rotates to a fresh session/continuity identity.",
      "A signed-out offline session remains playable locally while sync status stays truthful.",
    ],
    evidence: [
      { label: "Offline continuity #327", href: issue(327) },
      { label: "Continuity tests", href: source("AgendaTests/AgendaConversationStateTests.swift") },
    ],
  },
  {
    id: "UA-10",
    title: "Shape the Deck with filters, importance, order, and statistics",
    platform: "iPhone Deck",
    status: "shipped",
    statusLabel: "Importance landed in Build 32",
    releaseTruth:
      "Theme/date/source filters, presentation choices, session statistics, and 1–3 importance source exist. Final physical acceptance for the full importance issue remains open.",
    prerequisites: [
      "Use a synthetic audience with cards across two themes and importance levels 1, 2, and 3.",
    ],
    steps: [
      "Tap Filter cards.",
      "Choose one theme and a date/source filter; set minimum importance to 2+ and tap Done.",
      "Open filters again and set 3 only, then Reset deck view.",
      "Change the deck order or layout when present, tap Shuffle, then tap the statistics strip.",
    ],
    expected: [
      "Counts update before private prompts are revealed: 1+ includes all, 2+ includes 2 and 3, and 3 only includes 3.",
      "Changing filters never revives completed cards, switches audience, or mutates the active session outcome.",
      "Statistics and remaining-by-theme distribution match the currently eligible deck.",
    ],
    evidence: [
      { label: "Importance #398", href: issue(398) },
      { label: "Physical play milestone", href: milestone(12) },
    ],
  },
  {
    id: "UA-11",
    title: "Use private photos on face-down card backs",
    platform: "iPhone Deck · local only",
    status: "shipped",
    statusLabel: "Shipped in Build 5",
    releaseTruth:
      "Local photo-backed cards were physically exercised in historical Agenda! builds. They are intentionally not cloud-synced.",
    prerequisites: [
      "Use synthetic images with no faces, notifications, location, or private text.",
    ],
    steps: [
      "Open the card-back photo library from Deck.",
      "Choose two synthetic photos with the system picker and tap Done.",
      "Shuffle or advance several face-down cards, then reveal one.",
      "Return to the photo library and remove one Convoful copy.",
    ],
    expected: [
      "Photos decorate only face-down cards, avoid an immediate repeat when possible, and disappear completely on reveal.",
      "Removing a photo deletes only Convoful's copy; the original remains in Photos.",
      "The images do not appear on another device, in Search, Sync payloads, or recipient Library views.",
    ],
    evidence: [
      { label: "iOS architecture", href: source("docs/IOS_ARCHITECTURE.md") },
      { label: "Physical play milestone", href: milestone(12) },
    ],
  },
  {
    id: "UA-12",
    title: "Open the saved source only after Reveal",
    platform: "iPhone Deck and Manage",
    status: "source",
    statusLabel: "Build 37 source · physical coverage incomplete",
    releaseTruth:
      "The private saved-source reader is merged in current source. Broad real X/Instagram media, production delivery, offline cache, and exact-candidate device acceptance remain incomplete.",
    prerequisites: [
      "Use an authorized synthetic card with retained source text and safe media.",
    ],
    steps: [
      "On a face-down card, inspect visible and VoiceOver content; do not reveal yet.",
      "Reveal the card, then swipe strongly upward or tap the saved-source action.",
      "Read retained text, move through media, and close Saved source.",
      "Open Manage, select the same card, and open Saved source there.",
      "After one authenticated load, enable airplane mode and repeat only for media the app labels available offline.",
    ],
    expected: [
      "No source text, URL, image, video frame, or accessibility value exists before Reveal.",
      "Horizontal/downward gestures do not masquerade as the upward reader gesture.",
      "Only digest-verified, owner-bound cached media claims offline availability; corrupt or absent media fails closed with retry guidance.",
    ],
    evidence: [
      { label: "Reader #280", href: issue(280) },
      { label: "Rich media milestone", href: milestone(20) },
      { label: "Saved-source UI tests", href: source("AgendaUITests/AgendaSavedSourceReaderUITests.swift") },
    ],
  },
  {
    id: "UA-13",
    title: "Work offline, reconnect, and understand Sync",
    platform: "iPhone Sync",
    status: "in-progress",
    statusLabel: "Offline-first shipped · status consistency open",
    releaseTruth:
      "The app is designed to save locally and retry. The combined saved-share, mutation, offline, media, and cloud status still has an open P1 consistency issue.",
    prerequisites: [
      "Use a test account and a reversible synthetic card edit. Preserve all queues; never delete state to make a count look clean.",
    ],
    steps: [
      "Open Sync and record saved-share, local change, media, offline availability, and cloud refresh sections separately.",
      "Enable airplane mode, edit or create one synthetic card, and reopen Sync.",
      "Force-quit and relaunch while still offline; confirm the card remains usable.",
      "Disable airplane mode, tap the available retry/refresh action once, and wait for each named queue to settle.",
    ],
    expected: [
      "The local edit survives offline and relaunch without duplication.",
      "Pending local changes, saved shares, media uploads, offline readiness, and cloud failure are never conflated.",
      "A successful retry clears stale failure copy and another enrolled test device receives the authorized change.",
    ],
    evidence: [
      { label: "Sync truth #390", href: issue(390) },
      { label: "Sync verification", href: source("docs/CLOUDFLARE_SYNC_VERIFICATION.md") },
      { label: "Continuity milestone", href: milestone(10) },
    ],
  },
  {
    id: "UA-14",
    title: "Review the plan, Free allowance, and account controls",
    platform: "iPhone Manage → Plan & account",
    status: "source",
    statusLabel: "Permanent Free source · purchase proof open",
    releaseTruth:
      "Current source permits up to 10 Conversation Cards on the permanent Free plan and gates card 11. Real Checkout/Portal/purchase/reviewer acceptance is not complete.",
    prerequisites: [
      "Use a synthetic account. Do not make a real purchase during an ordinary regression run.",
    ],
    steps: [
      "Open Manage, then Plan & account.",
      "Review the Free allowance and current signed-in devices.",
      "Create cards until the warning begins at card 6; confirm cards 1–10 remain usable.",
      "Attempt card 11, choose Keep using my cards, and return to Manage.",
      "Test Refresh access and Restore access only with a reviewed fixture or sandbox account.",
    ],
    expected: [
      "Only creation of card 11 is gated; existing cards, Deck, Search, and History remain usable.",
      "No reviewer-only text appears to an ordinary customer.",
      "The app never claims paid access from a stale, wrong-owner, expired, or offline-ambiguous response.",
    ],
    evidence: [
      { label: "Free launch #620", href: issue(620) },
      { label: "Paid access #492", href: issue(492) },
      { label: "Paid model milestone", href: milestone(19) },
    ],
  },
  {
    id: "UA-15",
    title: "Request full account deletion and observe the pending state",
    platform: "iPhone Sync",
    status: "in-progress",
    statusLabel: "Initiation available · fulfillment gate open",
    releaseTruth:
      "In-app initiation, immediate session revocation, blocked sign-in, and pending copy exist. End-to-end operator fulfillment and completion delivery remain a first-submission gate.",
    prerequisites: [
      "Use only an explicitly disposable synthetic account. This is destructive to that account and must not be run against Mark's real account.",
      "Confirm the test plan includes the expected completion email and local-device data behavior.",
    ],
    steps: [
      "Open Sync and tap Request account deletion.",
      "Read the confirmation copy, including the completion window and local-data instructions, then confirm.",
      "Verify the app signs out, shows Deletion requested, and blocks another sign-in for that identity.",
      "Run the authorized operator fulfillment procedure, then verify one completion message at the verified account email.",
    ],
    expected: [
      "The request is for full account deletion, not deactivation, and all active sessions are revoked.",
      "The UI distinguishes server-side deletion from offline data that remains until the app is deleted on a device.",
      "One durable completion confirmation is delivered; a request alone is not reported as fulfillment.",
    ],
    evidence: [
      { label: "Submission deletion #496", href: issue(496) },
      { label: "Stricter deletion #445", href: issue(445) },
      { label: "Manual runbook", href: source("docs/ACCOUNT_DELETION_FIRST_SUBMISSION_MANUAL_RUNBOOK.md") },
    ],
  },
  {
    id: "UA-16",
    title: "Use the deliberately shared Library",
    platform: "Web, iPhone, and Android roadmap",
    status: "roadmap",
    statusLabel: "Partial platform slices · product acceptance open",
    releaseTruth:
      "Library domain, Web, and Android slices exist, but the complete multi-person, multi-platform Read/Listen/Watch journey is not accepted as a finished product.",
    prerequisites: [
      "Use two synthetic people in one test audience and a deliberately shared synthetic item.",
    ],
    steps: [
      "As the sender, save an item and explicitly choose Library for the test audience.",
      "As the recipient, open Library, choose All or Read/Listen/Watch, filter by sender, and open the source.",
      "Mark it Opened/In progress, then Finished or Dismissed.",
      "Return as the owner and verify the recipient never gained access to Conversation Cards, private Notes, Summary, keywords, or another audience.",
    ],
    expected: [
      "Only explicitly published fields cross the audience boundary.",
      "Library consumption state does not silently mark a Conversation Card discussed.",
      "If a platform lacks the controls, record Not available in this build/platform; this roadmap journey is not a release pass criterion yet.",
    ],
    evidence: [
      { label: "Library #29", href: issue(29) },
      { label: "Library milestone", href: milestone(13) },
      { label: "Library domain tests", href: source("LibraryDomain/Tests/LibraryDomainTests") },
    ],
  },
  {
    id: "UA-17",
    title: "Capture from Chrome with a browser extension",
    platform: "Chrome roadmap",
    status: "roadmap",
    statusLabel: "Someday maybe · not shipped",
    releaseTruth:
      "The repository contains extensive fail-closed extension prototypes/tests, but the public roadmap still treats browser capture as an unscheduled idea.",
    prerequisites: [
      "Do not install or test against a real profile unless a future release plan explicitly authorizes it.",
    ],
    steps: [
      "In a future reviewed build, open a safe page in Chrome and activate the Convoful extension.",
      "Review the exact visible text/media selected for capture, choose audience and destination, then Save.",
      "Open Convoful and verify one matching item with no hidden-page or cross-account content.",
    ],
    expected: [
      "Until a real extension ships, this journey is Not available—not failed.",
      "A future implementation must fail closed when visible-content, ownership, or destination authority cannot be proven.",
    ],
    evidence: [
      { label: "Extension #283", href: issue(283) },
      { label: "Browser extension milestone", href: milestone(21) },
      { label: "Extension browser tests", href: source("web-client/e2e/extension.spec.ts") },
    ],
  },
];

export const AUTOMATED_TEST_GROUPS: AutomatedTestGroup[] = [
  {
    title: "Native product logic and persistence",
    purpose:
      "Swift model/store tests cover cards, sessions, outcomes, continuity, imports, Sync, Search, onboarding, paid access, Free allowance, and release-history truth.",
    examples: [
      "Legacy decoding and migration",
      "atomic local writes and retry identity",
      "audience eligibility and outcome recovery",
      "owner-transition privacy fences",
    ],
    links: [
      { label: "AgendaTests", href: `${GITHUB}/tree/main/AgendaTests` },
      { label: "LibraryDomain tests", href: `${GITHUB}/tree/main/LibraryDomain/Tests` },
    ],
  },
  {
    title: "Human-like UI automation and accessibility",
    purpose:
      "XCUITest and Playwright drive visible controls for Deck, Manage, Search, onboarding, Share recovery, saved-source reading, web auth, Library, and public pages.",
    examples: [
      "VoiceOver-safe labels and private pre-reveal absence",
      "Accessibility XXXL and 200% zoom",
      "Reduce Motion and rapid-repeat input",
      "keyboard navigation and axe checks",
    ],
    links: [
      { label: "iOS UI tests", href: `${GITHUB}/tree/main/AgendaUITests` },
      { label: "Web browser tests", href: `${GITHUB}/tree/main/web-client/e2e` },
    ],
  },
  {
    title: "Web and Android application tests",
    purpose:
      "Vitest exercises Web domain/state/repository code; JVM tests cover Android deck, persistence, sharing, Search, Library projection, and Sync contracts.",
    examples: [
      "filters, capture, drafts, continuity, triage, auth, and version fences",
      "Android database, repository, deck, Search, Library, and share-draft behavior",
    ],
    links: [
      { label: "Web tests", href: `${GITHUB}/tree/main/web-client/tests` },
      { label: "Android tests", href: `${GITHUB}/tree/main/androidApp/src/test` },
    ],
  },
  {
    title: "API, authorization, and privacy/security contracts",
    purpose:
      "Worker-side scripts test auth, audiences, owner workspace, recipient Library, Search, Sync, media, sessions, account deletion, and Stripe without relying on UI behavior.",
    examples: [
      "forged principal/audience denial",
      "idempotency, replay, expiry, and signature checks",
      "private-field exclusion from recipient payloads",
      "incremental request-body limits and safe failure responses",
    ],
    links: [
      { label: "Worker test scripts", href: `${GITHUB}/tree/main/scripts` },
      { label: "Sync API suite", href: source("scripts/test_sync_api.mjs") },
      { label: "Webhook signature suite", href: source("scripts/test_stripe_webhook_signature.mjs") },
    ],
  },
  {
    title: "Search quality, performance probes, and race tests",
    purpose:
      "Deterministic evaluators compare ranking choices, negative judgments, embedding readiness, delivery races, and an approximate-index benchmark contract.",
    examples: [
      "fixed online/offline ranking weights",
      "stale-vector and wrong-owner rejection",
      "provider delivery races",
      "Vectorize recall/latency harness (no accepted live p50/p95 result yet)",
    ],
    links: [
      { label: "Search quality evidence", href: source("docs/SEARCH_QUALITY_EVALUATION.md") },
      { label: "Vector benchmark test", href: source("scripts/test_vectorize_search_benchmark.py") },
      { label: "Gesture performance #400", href: issue(400) },
    ],
  },
  {
    title: "Release, artifact, and deployment gates",
    purpose:
      "Scripts verify candidate provenance, archive/IPA privacy, screenshots, App Store package truth, Cloudflare configuration, gateway allowlists, and forward/rollback assets.",
    examples: [
      "known private-catalog digest/sentinel absence",
      "exact build/source/tree binding",
      "server-side noindex/no-store route behavior",
      "App Store fields remain false until receipt-backed",
    ],
    links: [
      { label: "Candidate verifier", href: source("scripts/verify_ios_app_store_candidate.sh") },
      { label: "Archive privacy verifier", href: source("scripts/verify_ios_public_archive.sh") },
      { label: "Web gateway tests", href: source("scripts/test_convoful_web_gateway.mjs") },
    ],
  },
];

export const COVERAGE_GAPS = [
  "No GitHub Actions workflows are configured: GitHub reported zero workflows on 10 August 2026, and recent pull requests had no reported checks. The repository has many tests, but they are not continuous CI until a workflow runs them automatically and blocks regressions.",
  "There is no accepted production-wide latency SLO, continuous synthetic latency monitor, or sustained load/soak test. Search has a benchmark harness; real Vectorize recall and p50/p95 latency remain unaccepted.",
  "Simulator and browser automation cannot replace exact-build physical iPhone evidence for gesture responsiveness, Share Sheet behavior, protected media, offline replay, account transitions, purchase, or App Review access.",
  "The complete ordinary-person path—clean install → signup → first card → cross-device Sync → deletion fulfillment—still needs one exact-candidate end-to-end acceptance run.",
  "Full multi-person Library acceptance, broad real source-media coverage, semantic production Search, Photo creation, real paid lifecycle, and the Chrome extension remain partial or roadmap work.",
] as const;

function escapeHTML(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function linksMarkup(links: ActivityLink[]): string {
  return links.map(({ label, href }) => (
    `<a href="${escapeHTML(href)}">${escapeHTML(label)}</a>`
  )).join("");
}

function list(items: string[]): string {
  return `<ol>${items.map((item) => `<li>${escapeHTML(item)}</li>`).join("")}</ol>`;
}

function journeyMarkup(journey: EssentialJourney): string {
  return `
    <details class="ua-journey" data-status="${journey.status}" data-journey-id="${journey.id}">
      <summary>
        <span class="ua-number">${journey.id}</span>
        <span class="ua-summary-copy">
          <strong>${escapeHTML(journey.title)}</strong>
          <span>${escapeHTML(journey.platform)}</span>
        </span>
        <span class="ua-status">${escapeHTML(journey.statusLabel)}</span>
      </summary>
      <div class="ua-journey-body">
        <p class="ua-release-truth"><strong>Release truth:</strong> ${escapeHTML(journey.releaseTruth)}</p>
        <div class="ua-journey-columns">
          <section><h3>Before you start</h3>${list(journey.prerequisites)}</section>
          <section><h3>Human actions</h3>${list(journey.steps)}</section>
          <section><h3>Pass when</h3>${list(journey.expected)}</section>
        </div>
        <nav class="ua-evidence" aria-label="Evidence for ${journey.id}">${linksMarkup(journey.evidence)}</nav>
      </div>
    </details>`;
}

function testGroupMarkup(group: AutomatedTestGroup): string {
  return `
    <article class="ua-test-group">
      <h3>${escapeHTML(group.title)}</h3>
      <p>${escapeHTML(group.purpose)}</p>
      <ul>${group.examples.map((item) => `<li>${escapeHTML(item)}</li>`).join("")}</ul>
      <nav aria-label="Sources for ${escapeHTML(group.title)}">${linksMarkup(group.links)}</nav>
    </article>`;
}

function setMetadata(): void {
  document.documentElement.dataset.surface = "user-activities";
  document.title = `User activities and testing · ${PRODUCT.name}`;
  let description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (!description) {
    description = document.createElement("meta");
    description.name = "description";
    document.head.append(description);
  }
  description.content = "Source-backed essential Convoful user journeys and separate automated test coverage.";
  let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.rel = "canonical";
    document.head.append(canonical);
  }
  canonical.href = `${WEB_ROUTES.canonicalOrigin}${WEB_ROUTES.userActivities}`;
}

export function renderUserActivities(root: HTMLElement): void {
  setMetadata();
  const counts = ESSENTIAL_JOURNEYS.reduce<Record<JourneyStatus, number>>(
    (result, journey) => {
      result[journey.status] += 1;
      return result;
    },
    { shipped: 0, source: 0, "in-progress": 0, roadmap: 0 },
  );

  root.innerHTML = `
    <main class="ua-shell" id="app-content">
      <header class="ua-site-header">
        <a class="ua-brand" href="${WEB_ROUTES.markAttention}"><span aria-hidden="true">C</span> Convoful · For Mark</a>
        <nav aria-label="For Mark testing navigation">
          <a href="#essential-journeys">Journeys</a>
          <a href="#automated-tests">Code tests</a>
          <a href="#coverage-gaps">Gaps</a>
        </nav>
      </header>

      <section class="ua-hero">
        <div>
          <p class="ua-eyebrow">Regression map · reconciled ${EVIDENCE_DATE}</p>
          <h1>What a person can do—and exactly how to test it.</h1>
          <p class="ua-lede">Essential journeys are the durable human contract. New work should add a journey or deepen one; it should not quietly break an older one.</p>
        </div>
        <aside class="ua-truth-card" aria-label="Evidence boundary">
          <strong>Read the badge before testing.</strong>
          <p><b>Previously shipped</b> means evidence exists in an installed historical build. <b>Current source</b> is not the same as TestFlight or physical acceptance. <b>In progress</b> and <b>roadmap</b> controls may be absent.</p>
        </aside>
      </section>

      <section class="ua-runbook" aria-labelledby="runbook-title">
        <div>
          <p class="ua-eyebrow">Before every human run</p>
          <h2 id="runbook-title">Record the test identity first.</h2>
        </div>
        <ol>
          <li>Record platform, product name, version/build, device/browser, OS, test account/fixture, network state, and date.</li>
          <li>Use synthetic content. Hide email addresses, card text, photos, notifications, tokens, and device identifiers from screenshots.</li>
          <li>Run only journeys whose badge matches the build. If a control is absent, record <b>Not available</b>; do not improvise.</li>
          <li>For a failure, record journey ID, exact failed step, expected versus actual result, reproducibility, and a privacy-safe screenshot.</li>
          <li>Never reset, reinstall, sign out everywhere, purchase, or delete an account unless that journey's prerequisites explicitly authorize a disposable fixture.</li>
        </ol>
        <p class="ua-result-template"><strong>Result template:</strong> <code>UA-__ · PASS / FAIL / BLOCKED / NOT AVAILABLE · build · device/OS · account fixture · network · failed step · issue link</code></p>
      </section>

      <section class="ua-summary-grid" aria-label="Journey status summary">
        <div><strong>${counts.shipped}</strong><span>previously shipped</span></div>
        <div><strong>${counts.source}</strong><span>current source, proof open</span></div>
        <div><strong>${counts["in-progress"]}</strong><span>in progress</span></div>
        <div><strong>${counts.roadmap}</strong><span>roadmap / partial</span></div>
      </section>

      <section class="ua-section" id="essential-journeys" aria-labelledby="journeys-title">
        <div class="ua-section-heading">
          <div><p class="ua-eyebrow">Human regression contract</p><h2 id="journeys-title">Essential journeys</h2></div>
          <p>Open a row for prerequisites, ordered actions, pass criteria, and canonical evidence.</p>
        </div>
        <div class="ua-journeys">${ESSENTIAL_JOURNEYS.map(journeyMarkup).join("")}</div>
      </section>

      <section class="ua-section ua-tests" id="automated-tests" aria-labelledby="tests-title">
        <div class="ua-section-heading">
          <div><p class="ua-eyebrow">Separate from human clicks</p><h2 id="tests-title">Automated code tests</h2></div>
          <p>These suites test contracts that are hard, unsafe, or wasteful to prove manually every time.</p>
        </div>
        <div class="ua-ci-warning">
          <strong>Repository tests are not continuous CI today.</strong>
          <p>GitHub reported <b>zero Actions workflows</b> on ${EVIDENCE_DATE}; a recent merged pull request reported no checks. Green local or PR evidence is valuable, but it does not automatically protect every new pull request.</p>
        </div>
        <div class="ua-test-grid">${AUTOMATED_TEST_GROUPS.map(testGroupMarkup).join("")}</div>
      </section>

      <section class="ua-section" id="coverage-gaps" aria-labelledby="gaps-title">
        <div class="ua-section-heading">
          <div><p class="ua-eyebrow">Do not overclaim</p><h2 id="gaps-title">Known coverage gaps</h2></div>
          <p>These are missing proof layers, not guesses that the product is broken.</p>
        </div>
        <ol class="ua-gaps">${COVERAGE_GAPS.map((gap) => `<li>${escapeHTML(gap)}</li>`).join("")}</ol>
      </section>

      <footer class="ua-footer">
        <p>Source snapshot: current main, live GitHub issues/milestones, live public roadmap, release history, and repository test inventory reconciled ${EVIDENCE_DATE}. The live roadmap still reported Build 34 and was last curated 4 August; current source has a Build 37 wrapper, so this page keeps those evidence layers separate.</p>
        <nav aria-label="Testing source links">
          <a href="${issue(653)}">Guide issue #653</a>
          <a href="${WEB_ROUTES.roadmap}">Public roadmap</a>
          <a href="${WEB_ROUTES.appStoreRelease}">App Store release</a>
          <a href="${issue(12)}">High-bar launch review</a>
        </nav>
      </footer>
    </main>`;
}
